import axios, { InternalAxiosRequestConfig } from 'axios'
import { getTokens } from './jwtStorage'
import { refreshAccessToken } from './jwtRefresh'
import { API_ERROR_FALLBACK, ApiError, envelopeMessage } from 'api/errors'

// Noted on every request by the request interceptor: the access token it left
// with (none before sign-in), and whether it is already the retry after a 401.
type TrackedRequestConfig = InternalAxiosRequestConfig & {
  _sentAccessToken?: string
  _retry?: boolean
}

const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete'])
const AUTH_REFRESH_EXCLUDED_PATHS = [
  '/auth/login',
  '/auth/respond-to-mfa',
  '/auth/refresh-token',
  '/auth/signup',
  '/auth/confirm-signup',
  '/auth/resend-code',
  '/auth/forgot-password',
  '/auth/reset-password'
]

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_GOLDIE_REST_ENDPOINT,
  // Array query params MUST repeat the key — product_ids=1&product_ids=2 —
  // which is the only form FastAPI's Query(List[int]) binds. axios defaults to
  // product_ids[]=1, a different key, so the API saw None and silently skipped
  // the filter: every vault and product graph plotted the same account-wide
  // series while the scoped today-point rode a scalar param, producing the
  // "value drops like a cliff" ending. Scalars are unaffected either way.
  paramsSerializer: { indexes: null }
})

axiosInstance.defaults.headers.common['X-Client'] = 'goldie-web/1.5.0'

axiosInstance.interceptors.request.use(config => {
  const tokens = getTokens()
  const tracked = config as TrackedRequestConfig
  tracked._sentAccessToken = tokens?.accessToken
  if (tokens) {
    config.headers.Authorization = `Bearer ${tokens.accessToken}`
  }
  const method = (config.method ?? '').toLowerCase()
  if (WRITE_METHODS.has(method) && !config.headers['Idempotency-Key']) {
    config.headers['Idempotency-Key'] = (
      crypto as Crypto & { randomUUID(): string }
    ).randomUUID()
  }
  return config
})

const isAuthPath = (url?: string): boolean => {
  const requestUrl = url ?? ''
  return AUTH_REFRESH_EXCLUDED_PATHS.some(path => requestUrl.includes(path))
}

axiosInstance.interceptors.response.use(
  response => {
    // Unwrap serverless envelope { status: boolean, data: T } so callers
    // receive the payload directly, matching the old Rails response shape.
    const d = response.data
    if (d !== null && typeof d === 'object' && 'status' in d) {
      const envelope = d as {
        status: boolean
        message?: string
        code?: string
        data?: { code?: string } | unknown
      }
      // The backend returns HTTP 200 with { status: false } for domain
      // failures (e.g. trading paused, expired review order). Without this the
      // envelope would unwrap to `data` (often null) and resolve as a SUCCESS,
      // firing onSuccess with null and showing the success toast on a failed
      // buy/sell. Reject so callers' onError/catch fire instead.
      if (envelope.status === false) {
        // Machine-readable error code: prefer the top-level `code`, fall back to
        // the legacy `data.code` (the Cognito auth router nests it there).
        const inner = envelope.data
        const nestedCode =
          inner !== null && typeof inner === 'object' && 'code' in inner
            ? (inner as { code?: string }).code
            : undefined
        const code = envelope.code ?? nestedCode ?? 'API_ERROR'
        // A blank or missing message would reach the screen as an empty error
        // line, so it becomes API_ERROR_FALLBACK, which says what to do next.
        return Promise.reject(
          new ApiError(
            envelopeMessage(envelope.message) ?? API_ERROR_FALLBACK,
            code,
            response.status,
            inner
          )
        )
      }
      if ('data' in envelope) {
        response.data = envelope.data
      }
    }
    return response
  },
  async error => {
    const request = error.config as TrackedRequestConfig | undefined
    if (
      error.response?.status !== 401 ||
      !request ||
      request._retry ||
      isAuthPath(request.url)
    ) {
      return Promise.reject(error)
    }

    // A refresh can only help a request that carried the customer's access
    // token. One that left without a token is refused whatever state the
    // session is in, even when its 401 lands after the customer has signed in:
    // the login page asks for /products before sign-in and React Query retries
    // it a second later. Refreshing for that retry is what put a refresh in
    // flight just as the customer moved on, and WebKit's cancelled refresh then
    // signed them out (see jwtRefresh.ts).
    const current = getTokens()
    if (!request._sentAccessToken || current === null) {
      return Promise.reject(error)
    }

    request._retry = true

    // The token was refreshed while this request was out: send it again with
    // the new one rather than start another refresh.
    if (current.accessToken !== request._sentAccessToken) {
      return axiosInstance(request)
    }

    // Concurrent 401s share the one refresh (refreshAccessToken is
    // single-flight).
    try {
      await refreshAccessToken()
    } catch {
      // jwtRefresh has settled the session: signed out if the API refused the
      // refresh token, kept with another try scheduled otherwise. Either way
      // this request fails with its own 401.
      return Promise.reject(error)
    }
    return axiosInstance(request)
  }
)

export default axiosInstance
